// @flow

import React, { Component, createRef } from 'react';
import styled from 'styled-components';
import { colors, fonts, formStyles } from 'shared/styleguide';
import config from 'main-app/config';

// ---------------------------------------------------------------------------
// Styled components — mirrors shared/components/form/Input.js exactly
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  position: relative;
  padding: 0 0 16px 0;
  margin: 0;
  width: 100%;

  input {
    padding: 0 10px;
    ${formStyles.field}
  }
`;

const FieldLabelEl = styled.label`
  display: block;
  ${fonts.bodyRegular}
  color: ${colors.charcoalGrey};
  padding-bottom: 8px;
`;

const FieldMessage = styled.div`
  margin-top: 4px;
  ${fonts.bodyRegular}
  color: ${colors.tomato};
  font-size: 12px;
`;

// ---------------------------------------------------------------------------
// Google Maps script loader — loads once, guards against double-loading
// ---------------------------------------------------------------------------

const LOADED_EVENT = 'google-maps-loaded';

function loadGoogleMapsScript(apiKey) {
  if (window.google && window.google.maps && window.google.maps.places) {
    return;
  }
  if (window.__googleMapsLoading) {
    return;
  }

  window.__googleMapsLoading = true;

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    window.__googleMapsLoading = false;
    window.dispatchEvent(new Event(LOADED_EVENT));
  };
  script.onerror = () => {
    window.__googleMapsLoading = false;
  };

  if (document.head) {
    document.head.appendChild(script);
  }
}

// ---------------------------------------------------------------------------
// Address parsing helper
// ---------------------------------------------------------------------------

function parsePlace(place) {
  const get = (types, nameType = 'long_name') => {
    const component = (place.address_components || []).find(c =>
      types.some(t => c.types.includes(t)),
    );
    return component ? component[nameType] : '';
  };

  const streetNumber = get(['street_number']);
  const route = get(['route']);
  const line1 = [streetNumber, route].filter(Boolean).join(' ');
  const city =
    get(['locality']) ||
    get(['sublocality_level_1']) ||
    get(['postal_town']);
  const state = get(['administrative_area_level_1'], 'short_name');
  const zip = get(['postal_code']);
  const country = get(['country'], 'short_name');

  return { line1, city, state, zip, country };
}

// ---------------------------------------------------------------------------
// Main component
//
// Formik v1 usage (inside a <Field component={AddressAutocomplete} .../>):
//   Receives field, form props from Formik, plus:
//     cityField, stateField, zipField  — Formik field names to auto-fill
//
// Plain usage (class components or non-Formik state):
//   Pass value, onChange, onAddressSelect directly (no field/form props)
// ---------------------------------------------------------------------------

type Props = {
  // Formik v1 injected props (present when used via <Field component={...}>)
  field?: { name: string, value: string, onChange: Function, onBlur: Function },
  form?: { setFieldValue: Function, setFieldTouched: Function },
  // Extra Formik fields to auto-fill on selection
  cityField?: string,
  stateField?: string,
  zipField?: string,
  // Shared display props
  label?: ?string,
  placeholder?: string,
  error?: ?string,
  style?: Object,
  // Plain mode props (when used without Formik)
  value?: string,
  onChange?: (value: string) => void,
  onAddressSelect?: (address: {
    line1: string,
    city: string,
    state: string,
    zip: string,
    country: string,
  }) => void,
};

class AddressAutocomplete extends Component<Props> {
  static defaultProps = {
    field: undefined,
    form: undefined,
    cityField: undefined,
    stateField: undefined,
    zipField: undefined,
    label: undefined,
    placeholder: undefined,
    error: undefined,
    style: undefined,
    value: undefined,
    onChange: undefined,
    onAddressSelect: undefined,
  };

  inputRef: any = createRef();
  autocomplete: any = null;
  _loadedListener: ?Function = null;

  componentDidMount() {
    const apiKey = config && config.googleMapsApiKey;
    if (!apiKey) return;

    if (window.google && window.google.maps && window.google.maps.places) {
      this.attachAutocomplete();
    } else {
      loadGoogleMapsScript(apiKey);
      this._loadedListener = () => this.attachAutocomplete();
      window.addEventListener(LOADED_EVENT, this._loadedListener, {
        once: true,
      });
    }
  }

  componentWillUnmount() {
    if (this._loadedListener) {
      window.removeEventListener(LOADED_EVENT, this._loadedListener);
    }
    if (this.autocomplete && window.google && window.google.maps) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }

  attachAutocomplete() {
    const inputEl = this.inputRef.current;
    if (!inputEl) return;
    if (!window.google || !window.google.maps || !window.google.maps.places)
      return;

    this.autocomplete = new window.google.maps.places.Autocomplete(inputEl, {
      types: ['address'],
    });

    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();
      if (!place || !place.address_components) return;
      this.handlePlaceSelect(parsePlace(place));
    });
  }

  handlePlaceSelect = ({ line1, city, state, zip, country }) => {
    const { field, form, cityField, stateField, zipField, onChange, onAddressSelect } =
      this.props;

    if (form && field) {
      // Formik mode
      if (line1) form.setFieldValue(field.name, line1);
      if (city && cityField) form.setFieldValue(cityField, city);
      if (state && stateField) form.setFieldValue(stateField, state);
      if (zip && zipField) form.setFieldValue(zipField, zip);
    } else {
      // Plain mode
      if (line1 && onChange) onChange(line1);
      if (onAddressSelect) onAddressSelect({ line1, city, state, zip, country });
    }
  };

  handleChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const { field, form, onChange } = this.props;
    const val = e.currentTarget.value;

    if (form && field) {
      form.setFieldValue(field.name, val);
    } else if (onChange) {
      onChange(val);
    }
  };

  handleBlur = () => {
    const { field, form } = this.props;
    if (form && field) {
      form.setFieldTouched(field.name, true);
    }
  };

  render() {
    const { field, form, label, placeholder, error, style } = this.props;
    const value =
      field
        ? (field.value || '')
        : (this.props.value || '');

    const fieldError =
      error ||
      (form && field && form.touched && form.touched[field.name] && form.errors && form.errors[field.name]) ||
      null;

    return (
      <Wrapper style={style}>
        {label && <FieldLabelEl>{label}</FieldLabelEl>}
        <input
          ref={this.inputRef}
          type="text"
          value={value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          placeholder={placeholder || ''}
          autoComplete="off"
        />
        {fieldError && <FieldMessage>{fieldError}</FieldMessage>}
      </Wrapper>
    );
  }
}

export default AddressAutocomplete;
